import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  AlertDialog,
  Button,
  Card,
  Flex,
  Grid,
  IconButton,
  Popover,
  Progress,
  Dialog,
  Select,
  Separator,
  Switch,
  Text,
  TextArea,
  TextField,
} from "@radix-ui/themes";
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Flame,
  Image,
  Lightbulb,
  MapPin,
  Plus,
  ChevronDown,
  RefreshCw,
  Search,
  Sparkles,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  useGetGoogleBusinessProfile,
  useGetGoogleBusinessProfileCategories,
  useUpdateGoogleBusinessProfileBasicInformation,
  useUpdateGoogleBusinessProfileContactLocation,
  useUpdateGoogleBusinessProfileOpeningHours,
  useUpdateGoogleBusinessProfileAttributes,
  useUpdateGoogleBusinessProfileSocialLinks,
  useGenerateGoogleBusinessProfileDescription,
} from "hooks";
import { useLocationContext, useProfileContext } from "context";
import { ToastAlert } from "components";
import { useToast } from "../../context/ToastContext";
import {
  hasSplitGoogleOpeningHours,
  normalizeGoogleOpeningPeriods,
} from "../../helpers/googleOpeningHours";

const createProfile = () => ({
  businessName: "",
  primaryCategory: "",
  primaryCategoryId: "",
  additionalCategories: [],
  additionalCategoryOptions: [],
  moreHoursTypes: [],
  description: "",
  phoneNumber: "",
  website: "",
  instagram: "",
  menuLink: "",
  menuLinkSupported: false,
  menuLinkLabel: "Menu link",
  photoCount: null,
  socialLinks: [],
  addressLine: "",
  storefrontAddress: {},
  address: "",
  googleUpdateStatus: {
    pendingFields: [],
    googleUpdatedFields: [],
    publishedProfile: {},
  },
});

const initialOpeningHours = [
  { day: "Monday", shortDay: "Mon", status: "Closed", opens: "09:00", closes: "17:00" },
  { day: "Tuesday", shortDay: "Tue", status: "Closed", opens: "09:00", closes: "17:00" },
  { day: "Wednesday", shortDay: "Wed", status: "Closed", opens: "09:00", closes: "17:00" },
  { day: "Thursday", shortDay: "Thu", status: "Closed", opens: "09:00", closes: "17:00" },
  { day: "Friday", shortDay: "Fri", status: "Closed", opens: "09:00", closes: "17:00" },
  { day: "Saturday", shortDay: "Sat", status: "Closed", opens: "09:00", closes: "17:00" },
  { day: "Sunday", shortDay: "Sun", status: "Closed", opens: "09:00", closes: "17:00" },
];

const createClosedHours = () => initialOpeningHours.map((item) => ({ ...item }));

const initialAttributes = [];

const DAYS = [
  ["MONDAY", "Monday", "Mon"],
  ["TUESDAY", "Tuesday", "Tue"],
  ["WEDNESDAY", "Wednesday", "Wed"],
  ["THURSDAY", "Thursday", "Thu"],
  ["FRIDAY", "Friday", "Fri"],
  ["SATURDAY", "Saturday", "Sat"],
  ["SUNDAY", "Sunday", "Sun"],
];

const SECTION_GOOGLE_FIELDS = {
  basic: ["title", "categories", "profile"],
  contact: ["phoneNumbers", "websiteUri", "storefrontAddress"],
  hours: ["regularHours", "specialHours", "moreHours"],
  attributes: [],
  social: [],
};

const GOOGLE_FIELD_DETAILS = {
  title: { label: "business name", profileKey: "businessName" },
  categories: { label: "categories", profileKey: "primaryCategory" },
  profile: { label: "business description", profileKey: "description" },
  phoneNumbers: { label: "phone number", profileKey: "phoneNumber" },
  websiteUri: { label: "website", profileKey: "website" },
  storefrontAddress: { label: "address", profileKey: "address" },
  regularHours: { label: "opening hours" },
  specialHours: { label: "special hours" },
  moreHours: { label: "additional hours" },
};

const SOCIAL_LINK_RULES = {
  "attributes/url_menu": {
    label: "Menu",
    example: "https://yourbusiness.com/menu",
    isValidPath: ({ pathParts, url }) =>
      pathParts.length > 0 && !url.hash,
  },
  "attributes/url_facebook": {
    label: "Facebook",
    hosts: ["facebook.com", "www.facebook.com"],
    example: "https://www.facebook.com/username",
    isValidPath: ({ pathParts, url }) =>
      pathParts.length === 1 && !url.search && !url.hash,
  },
  "attributes/url_instagram": {
    label: "Instagram",
    hosts: ["instagram.com", "www.instagram.com"],
    example: "https://www.instagram.com/username",
    isValidPath: ({ pathParts, url }) =>
      pathParts.length === 1 && !url.search && !url.hash,
  },
  "attributes/url_linkedin": {
    label: "LinkedIn",
    hosts: ["linkedin.com", "www.linkedin.com"],
    example: "https://www.linkedin.com/company/username",
    isValidPath: ({ pathParts, url }) =>
      pathParts.length === 2 &&
      ["in", "company"].includes(pathParts[0].toLowerCase()) &&
      !url.search &&
      !url.hash,
  },
  "attributes/url_tiktok": {
    label: "TikTok",
    hosts: ["tiktok.com", "www.tiktok.com"],
    example: "https://www.tiktok.com/@username",
    isValidPath: ({ pathParts, url }) =>
      pathParts.length === 1 && !url.search && !url.hash,
  },
  "attributes/url_twitter": {
    label: "X",
    hosts: ["twitter.com", "www.twitter.com"],
    example: "https://www.twitter.com/username",
    isValidPath: ({ pathParts, url }) =>
      pathParts.length === 1 && !url.search && !url.hash,
  },
  "attributes/url_whatsapp": {
    label: "WhatsApp",
    hosts: ["wa.me", "whatsapp.com", "www.whatsapp.com", "api.whatsapp.com"],
    example: "https://wa.me/441234567890",
    isValidPath: ({ pathParts, url }) => {
      if (url.hostname.toLowerCase() === "wa.me") {
        return pathParts.length === 1 && /^\d+$/.test(pathParts[0]) && !url.search;
      }
      return (
        pathParts.length === 1 &&
        pathParts[0].toLowerCase() === "send" &&
        Boolean(url.searchParams.get("phone"))
      );
    },
  },
  "attributes/url_youtube": {
    label: "YouTube",
    hosts: ["youtube.com", "www.youtube.com"],
    example: "https://www.youtube.com/@username",
    isValidPath: ({ pathParts, url }) =>
      !url.search &&
      !url.hash &&
      ((pathParts.length === 1 && pathParts[0].startsWith("@")) ||
        (pathParts.length === 2 &&
          ["channel", "user"].includes(pathParts[0].toLowerCase()))),
  },
};

function getSocialLinkError(link) {
  const value = link.value.trim();
  if (!value) return "";

  const rule = SOCIAL_LINK_RULES[link.name];
  try {
    const parsedUrl = new URL(value);
    const pathParts = parsedUrl.pathname.split("/").filter(Boolean);
    if (
      parsedUrl.protocol !== "https:" ||
      !rule ||
      (rule.hosts && !rule.hosts.includes(parsedUrl.hostname.toLowerCase())) ||
      !rule.isValidPath({ pathParts, url: parsedUrl })
    ) {
      throw new Error("Invalid social link");
    }
  } catch {
    return rule
      ? `Enter a valid ${rule.label} URL, for example ${rule.example}`
      : "Enter a valid HTTPS URL.";
  }

  return "";
}

function formatGoogleTime(time, fallback) {
  if (!time) return fallback;
  return `${String(time.hours ?? 0).padStart(2, "0")}:${String(
    time.minutes || 0
  ).padStart(2, "0")}`;
}

function mapGoogleOpeningHours(periods = []) {
  const normalizedPeriods = normalizeGoogleOpeningPeriods(periods);
  return DAYS.map(([googleDay, day, shortDay]) => {
    const period = normalizedPeriods.find((item) => item.openDay === googleDay);
    return {
      day,
      shortDay,
      status: period ? "Open" : "Closed",
      opens: formatGoogleTime(period?.openTime, "09:00"),
      closes: formatGoogleTime(period?.closeTime, "17:00"),
    };
  });
}

function mapGoogleMoreHours(moreHours = [], moreHoursTypes = []) {
  const typeLabels = new Map(
    moreHoursTypes.map((item) => [item.hoursTypeId, item.displayName])
  );
  return moreHours.map((item) => ({
    hoursTypeId: item.hoursTypeId,
    displayName:
      typeLabels.get(item.hoursTypeId) ||
      item.hoursTypeId.replaceAll("_", " ").toLowerCase().replace(/\b\w/g, (letter) =>
        letter.toUpperCase()
      ),
    hours: mapGoogleOpeningHours(item.periods),
  }));
}

function formatGoogleDate(date) {
  if (!date?.year || !date?.month || !date?.day) return "";
  return `${date.year}-${String(date.month).padStart(2, "0")}-${String(
    date.day
  ).padStart(2, "0")}`;
}

function mapGoogleSpecialHours(periods = []) {
  return periods
    .map((period) => ({
      date: formatGoogleDate(period.startDate),
      status: period.closed ? "Closed" : "Open",
      opens: formatGoogleTime(period.openTime, "09:00"),
      closes: formatGoogleTime(period.closeTime, "17:00"),
    }))
    .filter((period) => period.date);
}

function mapGoogleAttributes(attributes = []) {
  return attributes
    .filter((attribute) => typeof attribute.values?.[0] === "boolean")
    .map((attribute) => {
      const rawName = attribute.name?.split("/").pop() || "Attribute";
      const label = rawName
        .replace(/^(has_|is_)/, "")
        .replaceAll("_", " ")
        .replace(/\b\w/g, (character) => character.toUpperCase());
      return {
        name: attribute.name,
        label,
        enabled: attribute.values[0],
      };
    });
}

function CategoryPicker({
  organisationId,
  locationId,
  regionCode,
  languageCode,
  value,
  placeholder,
  excludedNames = [],
  onSelect,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timeoutId = window.setTimeout(
      () => setDebouncedSearch(searchText.trim()),
      300
    );
    return () => window.clearTimeout(timeoutId);
  }, [searchText]);

  const { data, isLoading, isError } = useGetGoogleBusinessProfileCategories(
    {
      organisationId,
      locationId,
      regionCode,
      languageCode,
      search: debouncedSearch,
    },
    isOpen
  );
  const categories = (data?.data?.categories || []).filter(
    (category) => !excludedNames.includes(category.name)
  );

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger>
        <Button
          type="button"
          variant="outline"
          color="gray"
          style={{ width: "100%", justifyContent: "space-between" }}
        >
          <Text truncate>{value?.displayName || placeholder}</Text>
          <ChevronDown size={14} />
        </Button>
      </Popover.Trigger>
      <Popover.Content
        align="start"
        style={{ width: 360, maxWidth: "calc(100vw - 32px)", padding: 0 }}
      >
        <Flex direction="column">
          <Flex p="2">
            <TextField.Root
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              placeholder="Search Google categories"
              style={{ width: "100%" }}
            >
              <TextField.Slot>
                <Search size={14} />
              </TextField.Slot>
            </TextField.Root>
          </Flex>
          <Separator size="4" />
          <Flex
            direction="column"
            p="2"
            style={{ maxHeight: 280, overflowY: "auto" }}
          >
            {isLoading ? (
              <Text size="2" color="gray" p="3" align="center">
                Loading categories...
              </Text>
            ) : isError ? (
              <Text size="2" color="red" p="3" align="center">
                Categories could not be loaded.
              </Text>
            ) : categories.length === 0 ? (
              <Text size="2" color="gray" p="3" align="center">
                No matching categories found.
              </Text>
            ) : (
              categories.map((category) => (
                <Button
                  key={category.name}
                  type="button"
                  variant="ghost"
                  color="gray"
                  onClick={() => {
                    onSelect(category);
                    setSearchText("");
                    setIsOpen(false);
                  }}
                  style={{ justifyContent: "flex-start" }}
                >
                  {category.displayName}
                </Button>
              ))
            )}
          </Flex>
        </Flex>
      </Popover.Content>
    </Popover.Root>
  );
}

function SectionLabel({ children }) {
  return (
    <Text
      as="label"
      size="1"
      weight="medium"
      color="gray"
      style={{ letterSpacing: "0.04em", textTransform: "uppercase" }}
    >
      {children}
    </Text>
  );
}

function ProfileCard({ title, action, children }) {
  return (
    <Card size="3">
      <Flex direction="column" gap="4">
        <Flex align="center" justify="between" gap="3">
          <Text size="3" weight="bold">
            {title}
          </Text>
          {action}
        </Flex>
        {children}
      </Flex>
    </Card>
  );
}

function GoogleSyncNotice({ pendingFields, publishedProfile }) {
  if (pendingFields.length === 0) return null;

  const fieldDetails = pendingFields
    .map((field) => GOOGLE_FIELD_DETAILS[field])
    .filter(Boolean);
  const fieldLabels = fieldDetails.map((field) => field.label);
  const publishedValue = fieldDetails
    .map((field) => field.profileKey && publishedProfile?.[field.profileKey])
    .find(Boolean);

  return (
    <Flex
      align="start"
      gap="2"
      p="3"
      style={{
        borderRadius: "var(--radius-3)",
        background: "var(--blue-a3)",
        border: "1px solid var(--blue-a5)",
      }}
    >
      <RefreshCw
        size={16}
        color="var(--blue-9)"
        style={{ flexShrink: 0, marginTop: 2 }}
      />
      <Flex direction="column" gap="1">
        <Text size="2" weight="bold" color="blue">
          Pending review by Google
        </Text>
        <Text size="2" color="gray">
          {`Changes to ${fieldLabels.join(" and ")} are still being reviewed. Google Search and Maps may continue showing the previous information.`}
        </Text>
        {publishedValue && (
          <Text size="1" color="gray">
            Google is currently showing: {publishedValue}
          </Text>
        )}
      </Flex>
    </Flex>
  );
}

function CompletionRing({ strength }) {
  return (
    <Flex
      align="center"
      justify="center"
      style={{
        width: 82,
        height: 82,
        borderRadius: "50%",
        background: `conic-gradient(var(--blue-9) ${strength}%, var(--gray-a4) 0)`,
      }}
    >
      <Flex
        align="center"
        justify="center"
        style={{
          width: 66,
          height: 66,
          borderRadius: "50%",
          background: "var(--color-panel-solid)",
        }}
      >
        <Text size="5" weight="bold" color="blue">
          {strength}%
        </Text>
      </Flex>
    </Flex>
  );
}

function EditButton({ disabled, onClick }) {
  return (
    <Button size="1" variant="outline" type="button" onClick={onClick} disabled={disabled}>
      Edit
    </Button>
  );
}

function SectionActions({ canEdit = true, isEditing, isSaving, onEdit, onCancel, onSave }) {
  return isEditing ? (
    <Flex gap="2">
      <Button size="1" variant="soft" color="gray" type="button" onClick={onCancel} disabled={isSaving}>
        Cancel
      </Button>
      <Button size="1" type="button" onClick={onSave} disabled={isSaving}>
        {isSaving ? "Saving..." : "Save"}
      </Button>
    </Flex>
  ) : (
    <EditButton onClick={onEdit} disabled={!canEdit} />
  );
}

function ReadOnlyField({ label, children, strong = false }) {
  return (
    <Flex direction="column" gap="2">
      <SectionLabel>{label}</SectionLabel>
      <Text size="3" weight={strong ? "bold" : "regular"} style={{ whiteSpace: "pre-wrap" }}>
        {children}
      </Text>
    </Flex>
  );
}

function SectionLoading() {
  return (
    <Flex align="center" gap="2" py="3">
      <RefreshCw size={15} color="var(--gray-9)" />
      <Text size="2" color="gray">Loading...</Text>
    </Flex>
  );
}

function formatTime(value) {
  const [rawHour, minutes] = value.split(":");
  const hour = Number(rawHour);
  const suffix = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${suffix}`;
}

function WeeklyHoursEditor({ hours, onChange }) {
  return (
    <Flex direction="column" gap="3">
      {hours.map((item, index) => (
        <Flex key={item.day} direction="column" gap="1">
          <Flex align="center" gap="3" wrap="wrap">
            <Text size="2" weight="bold" style={{ width: 72 }}>
              {item.shortDay}
            </Text>
            <Select.Root
              value={item.status}
              onValueChange={(value) => onChange(index, "status", value)}
            >
              <Select.Trigger style={{ width: 92 }} />
              <Select.Content>
                <Select.Item value="Open">Open</Select.Item>
                <Select.Item value="Closed">Closed</Select.Item>
              </Select.Content>
            </Select.Root>
            <TextField.Root
              type="time"
              step="60"
              value={item.opens}
              disabled={item.status === "Closed"}
              onChange={(event) => onChange(index, "opens", event.target.value)}
              aria-label={`${item.day} opening time`}
              style={{ width: 120 }}
            />
            <Text size="2" color="gray">to</Text>
            <TextField.Root
              type="time"
              step="60"
              value={item.closes}
              disabled={item.status === "Closed"}
              onChange={(event) => onChange(index, "closes", event.target.value)}
              aria-label={`${item.day} closing time`}
              style={{ width: 120 }}
            />
          </Flex>
          {index > 0 && item.status === "Open" && (
            <Button
              size="1"
              variant="ghost"
              type="button"
              onClick={() => {
                onChange(index, "opens", hours[0].opens);
                onChange(index, "closes", hours[0].closes);
              }}
              style={{ alignSelf: "flex-start", marginLeft: 84 }}
            >
              Same as Monday
            </Button>
          )}
        </Flex>
      ))}
    </Flex>
  );
}

function WeeklyHoursList({ hours }) {
  return (
    <Flex direction="column">
      {hours.map((item, index) => (
        <Flex
          key={item.day}
          align="center"
          py="2"
          style={{
            borderBottom:
              index < hours.length - 1 ? "1px solid var(--gray-a4)" : "none",
          }}
        >
          <Text size="2" weight="medium" style={{ width: 100 }}>
            {item.day}
          </Text>
          <Text size="2">
            {item.status === "Closed"
              ? "Closed"
              : `${formatTime(item.opens)} – ${formatTime(item.closes)}`}
          </Text>
        </Flex>
      ))}
    </Flex>
  );
}

export default function ReviewsBusinessProfile() {
  const navigate = useNavigate();
  const { userProfile } = useProfileContext();
  const { locationsData = [] } = useLocationContext();
  const { setToastMessage } = useToast();
  const firstLocationId = locationsData[0]?.id?.toString() || "preview";
  const [selectedLocationId, setSelectedLocationId] = useState(firstLocationId);
  const selectedLocation = useMemo(
    () =>
      locationsData.find(
        (location) => location.id.toString() === selectedLocationId
      ) || locationsData[0],
    [locationsData, selectedLocationId]
  );
  const initialProfile = createProfile();
  const [profileOverrides, setProfileOverrides] = useState({});
  const [attributes, setAttributes] = useState(initialAttributes);
  const [hours, setHours] = useState(createClosedHours);
  const [moreHours, setMoreHours] = useState([]);
  const [specialHours, setSpecialHours] = useState([]);
  const [specialHoursDraft, setSpecialHoursDraft] = useState([]);
  const [isSpecialHoursDialogOpen, setIsSpecialHoursDialogOpen] = useState(false);
  const [editingSections, setEditingSections] = useState({});
  const [sectionSnapshots, setSectionSnapshots] = useState({});
  const [savingSection, setSavingSection] = useState(null);
  const [isNameChangeDialogOpen, setIsNameChangeDialogOpen] = useState(false);
  const [isDescriptionDialogOpen, setIsDescriptionDialogOpen] = useState(false);
  const [descriptionInstructions, setDescriptionInstructions] = useState("");
  const [descriptionSuggestion, setDescriptionSuggestion] = useState("");
  const [descriptionGenerationError, setDescriptionGenerationError] = useState("");
  const updateBasicInformation = useUpdateGoogleBusinessProfileBasicInformation();
  const updateContactLocation = useUpdateGoogleBusinessProfileContactLocation();
  const updateOpeningHours = useUpdateGoogleBusinessProfileOpeningHours();
  const updateAttributes = useUpdateGoogleBusinessProfileAttributes();
  const updateSocialLinks = useUpdateGoogleBusinessProfileSocialLinks();
  const generateDescription = useGenerateGoogleBusinessProfileDescription();
  const {
    data: businessProfileResponse,
    isLoading: isBusinessProfileLoading,
    error: businessProfileError,
    refetch: refetchBusinessProfile,
  } = useGetGoogleBusinessProfile(
    userProfile?.organisation_id,
    selectedLocationId,
    selectedLocationId !== "preview"
  );

  useEffect(() => {
    if (selectedLocationId === "preview" && locationsData[0]?.id) {
      setSelectedLocationId(locationsData[0].id.toString());
    }
  }, [locationsData, selectedLocationId]);

  useEffect(() => {
    const googleProfile = businessProfileResponse?.data?.profile;
    if (!googleProfile) return;

    setProfileOverrides((current) => ({
      ...current,
      [selectedLocationId]: {
        ...createProfile(),
        ...googleProfile,
      },
    }));
    setHours(mapGoogleOpeningHours(googleProfile.openingHours));
    setMoreHours(
      mapGoogleMoreHours(googleProfile.moreHours, googleProfile.moreHoursTypes)
    );
    setSpecialHours(mapGoogleSpecialHours(googleProfile.specialHours));

    const googleAttributes = mapGoogleAttributes(googleProfile.attributes);
    setAttributes(googleAttributes.length > 0 ? googleAttributes : []);
  }, [businessProfileResponse, selectedLocationId]);

  const profile = {
    ...initialProfile,
    ...(profileOverrides[selectedLocationId] || {}),
  };
  const needsMorePhotos =
    profile.photoCount !== null && Number(profile.photoCount) < 5;
  const profileChecklist = [
    ["Business name", Boolean(profile.businessName)],
    ["Address", Boolean(profile.address)],
    ["Phone number", Boolean(profile.phoneNumber)],
    ["Opening hours", hours.some((item) => item.status === "Open")],
    ["Business description", Boolean(profile.description)],
    ["Photos (min 5)", Number(profile.photoCount || 0) >= 5],
    ["Menu/service link", Boolean(profile.menuLink)],
  ];
  const profileStrength = Math.round(
    (profileChecklist.filter(([, complete]) => complete).length /
      profileChecklist.length) *
      100
  );
  const googleOpeningPeriods = profile.openingHours || [];
  const hasSplitOpeningHours = hasSplitGoogleOpeningHours(googleOpeningPeriods);
  const hasSplitMoreHours = (profile.moreHours || []).some(
    (item) => hasSplitGoogleOpeningHours(item.periods || [])
  );
  const hasSplitSpecialHours =
    new Set(specialHours.map((item) => item.date)).size !== specialHours.length;
  const availableMoreHoursTypes = (profile.moreHoursTypes || []).filter(
    (type) => !moreHours.some((item) => item.hoursTypeId === type.hoursTypeId)
  );
  const googleUpdateStatus = profile.googleUpdateStatus || {};
  const googlePendingFields = googleUpdateStatus.pendingFields || [];
  const editableAddressPreview = [
    profile.addressLine,
    profile.storefrontAddress?.locality,
    profile.storefrontAddress?.administrativeArea,
    profile.storefrontAddress?.postalCode,
  ]
    .filter(Boolean)
    .join(", ");
  const pendingFieldsForSection = (section) =>
    (SECTION_GOOGLE_FIELDS[section] || []).filter((sectionField) =>
      googlePendingFields.some(
        (pendingField) =>
          pendingField === sectionField ||
          pendingField.startsWith(`${sectionField}.`)
      )
    );

  const showSectionSavedToast = (label) => {
    setToastMessage(
      `${label} saved to Google. Search and Maps may take time to update.`
    );
  };

  const updateProfile = (field, value) => {
    setProfileOverrides((current) => ({
      ...current,
      [selectedLocationId]: {
        ...(current[selectedLocationId] || {}),
        [field]: value,
      },
    }));
  };

  const updateProfileFields = (fields) => {
    setProfileOverrides((current) => ({
      ...current,
      [selectedLocationId]: {
        ...(current[selectedLocationId] || {}),
        ...fields,
      },
    }));
  };

  const updateStorefrontAddress = (field, value) => {
    updateProfile("storefrontAddress", {
      ...profile.storefrontAddress,
      [field]: value,
    });
  };

  const startEditing = (section) => {
    setSectionSnapshots((current) => ({
      ...current,
      [section]: {
        profile: { ...profile },
        hours: hours.map((item) => ({ ...item })),
        moreHours: moreHours.map((item) => ({
          ...item,
          hours: item.hours.map((day) => ({ ...day })),
        })),
        attributes: attributes.map((item) => ({ ...item })),
      },
    }));
    setEditingSections((current) => ({ ...current, [section]: true }));
  };

  const cancelEditing = (section) => {
    const snapshot = sectionSnapshots[section];
    if (snapshot?.profile) {
      setProfileOverrides((current) => ({
        ...current,
        [selectedLocationId]: snapshot.profile,
      }));
    }
    if (snapshot?.hours) {
      setHours(snapshot.hours);
    }
    if (snapshot?.moreHours) {
      setMoreHours(snapshot.moreHours);
    }
    if (snapshot?.attributes) {
      setAttributes(snapshot.attributes);
    }
    setEditingSections((current) => ({ ...current, [section]: false }));
  };

  const refreshProfileAfterSave = async () => {
    const refreshedResponse = await refetchBusinessProfile();
    const googleProfile = refreshedResponse.data?.data?.profile;
    if (!googleProfile) {
      throw new Error("Google returned no refreshed business profile data.");
    }

    setProfileOverrides((current) => ({
      ...current,
      [selectedLocationId]: {
        ...createProfile(),
        ...googleProfile,
      },
    }));
    setHours(mapGoogleOpeningHours(googleProfile.openingHours));
    setMoreHours(
      mapGoogleMoreHours(googleProfile.moreHours, googleProfile.moreHoursTypes)
    );
    setSpecialHours(mapGoogleSpecialHours(googleProfile.specialHours));
    setAttributes(mapGoogleAttributes(googleProfile.attributes));
  };

  const saveSection = async (section, label) => {
    if (section === "basic") {
      const originalProfile = sectionSnapshots.basic?.profile || {};
      const basicChanges = {
        organisation_id: userProfile.organisation_id,
      };
      if (profile.businessName !== originalProfile.businessName) {
        basicChanges.business_name = profile.businessName;
      }
      if (profile.description !== originalProfile.description) {
        basicChanges.description = profile.description;
      }

      const currentCategoryIds = profile.additionalCategoryOptions.map(
        (category) => category.name
      );
      const originalCategoryIds = (
        originalProfile.additionalCategoryOptions || []
      ).map((category) => category.name);
      if (
        profile.primaryCategoryId !== originalProfile.primaryCategoryId ||
        JSON.stringify(currentCategoryIds) !== JSON.stringify(originalCategoryIds)
      ) {
        basicChanges.primary_category_id = profile.primaryCategoryId;
        basicChanges.additional_category_ids = currentCategoryIds;
      }

      if (Object.keys(basicChanges).length === 1) {
        setEditingSections((current) => ({ ...current, [section]: false }));
        setToastMessage("No basic information changes to save");
        return;
      }

      setSavingSection(section);
      try {
        await updateBasicInformation.mutateAsync({
          locationId: selectedLocationId,
          data: basicChanges,
        });
        await refreshProfileAfterSave();
        setEditingSections((current) => ({ ...current, [section]: false }));
        showSectionSavedToast(label);
      } catch (error) {
        setToastMessage(
          error.response?.data?.message || `Failed to update ${label.toLowerCase()}`
        );
      } finally {
        setSavingSection(null);
      }
      return;
    }

    if (section === "contact") {
      const originalProfile = sectionSnapshots.contact?.profile || {};
      const contactChanges = {
        organisation_id: userProfile.organisation_id,
      };
      if (profile.phoneNumber !== originalProfile.phoneNumber) {
        contactChanges.phone_number = profile.phoneNumber;
      }
      if (profile.website !== originalProfile.website) {
        contactChanges.website = profile.website;
      }

      const currentAddress = profile.storefrontAddress?.regionCode
        ? {
            ...profile.storefrontAddress,
            addressLines: [profile.addressLine],
          }
        : null;
      const originalAddress = originalProfile.storefrontAddress?.regionCode
        ? {
            ...originalProfile.storefrontAddress,
            addressLines: [originalProfile.addressLine],
          }
        : null;
      if (JSON.stringify(currentAddress) !== JSON.stringify(originalAddress)) {
        contactChanges.storefront_address = currentAddress;
      }

      if (Object.keys(contactChanges).length === 1) {
        setEditingSections((current) => ({ ...current, [section]: false }));
        setToastMessage("No contact or location changes to save");
        return;
      }

      setSavingSection(section);
      try {
        await updateContactLocation.mutateAsync({
          locationId: selectedLocationId,
          data: contactChanges,
        });
        await refreshProfileAfterSave();
        setEditingSections((current) => ({ ...current, [section]: false }));
        showSectionSavedToast(label);
      } catch (error) {
        setToastMessage(
          error.response?.data?.message || `Failed to update ${label.toLowerCase()}`
        );
      } finally {
        setSavingSection(null);
      }
      return;
    }

    if (section === "hours") {
      const originalHours = sectionSnapshots.hours?.hours || [];
      const originalMoreHours = sectionSnapshots.hours?.moreHours || [];
      const regularHoursChanged =
        JSON.stringify(hours) !== JSON.stringify(originalHours);
      const moreHoursChanged =
        JSON.stringify(moreHours) !== JSON.stringify(originalMoreHours);
      if (!regularHoursChanged && !moreHoursChanged) {
        setEditingSections((current) => ({ ...current, [section]: false }));
        setToastMessage("No opening hours changes to save");
        return;
      }

      setSavingSection(section);
      try {
        const hoursChanges = {
          organisation_id: userProfile.organisation_id,
        };
        if (regularHoursChanged) {
          hoursChanges.hours = hours.map((item, index) => ({
            day: DAYS[index][0],
            status: item.status,
            opens: item.opens,
            closes: item.closes,
          }));
        }
        if (moreHoursChanged) {
          hoursChanges.more_hours = moreHours.map((moreHoursItem) => ({
            hours_type_id: moreHoursItem.hoursTypeId,
            hours: moreHoursItem.hours.map((item, index) => ({
              day: DAYS[index][0],
              status: item.status,
              opens: item.opens,
              closes: item.closes,
            })),
          }));
        }

        await updateOpeningHours.mutateAsync({
          locationId: selectedLocationId,
          data: hoursChanges,
        });
        await refreshProfileAfterSave();
        setEditingSections((current) => ({ ...current, [section]: false }));
        showSectionSavedToast(label);
      } catch (error) {
        setToastMessage(
          error.response?.data?.message || `Failed to update ${label.toLowerCase()}`
        );
      } finally {
        setSavingSection(null);
      }
      return;
    }

    if (section === "attributes") {
      const originalAttributes = new Map(
        (sectionSnapshots.attributes?.attributes || []).map((attribute) => [
          attribute.name,
          attribute.enabled,
        ])
      );
      const changedAttributes = attributes.filter(
        (attribute) =>
          attribute.enabled !== originalAttributes.get(attribute.name)
      );
      if (changedAttributes.length === 0) {
        setEditingSections((current) => ({ ...current, [section]: false }));
        setToastMessage("No attribute changes to save");
        return;
      }

      setSavingSection(section);
      try {
        await updateAttributes.mutateAsync({
          locationId: selectedLocationId,
          data: {
            organisation_id: userProfile.organisation_id,
            attributes: changedAttributes.map((attribute) => ({
              name: attribute.name,
              value: attribute.enabled,
            })),
          },
        });
        await refreshProfileAfterSave();
        setEditingSections((current) => ({ ...current, [section]: false }));
        showSectionSavedToast(label);
      } catch (error) {
        setToastMessage(
          error.response?.data?.message || `Failed to update ${label.toLowerCase()}`
        );
      } finally {
        setSavingSection(null);
      }
      return;
    }

    if (section === "social") {
      try {
        const editableLinks = [
          ...profile.socialLinks,
          ...(profile.menuLinkSupported
            ? [{ name: "attributes/url_menu", value: profile.menuLink }]
            : []),
        ];
        const invalidLink = editableLinks.find((link) =>
          Boolean(getSocialLinkError(link))
        );
        if (invalidLink) {
          setToastMessage(getSocialLinkError(invalidLink));
          return;
        }

        const originalLinks =
          sectionSnapshots.social?.profile?.socialLinks || [];
        const originalValues = new Map(
          originalLinks.map((link) => [link.name, link.value])
        );
        const changedLinks = profile.socialLinks.filter(
          (link) => link.value !== (originalValues.get(link.name) || "")
        );
        const originalMenuLink =
          sectionSnapshots.social?.profile?.menuLink || "";
        if (
          profile.menuLinkSupported &&
          profile.menuLink !== originalMenuLink
        ) {
          changedLinks.push({
            name: "attributes/url_menu",
            value: profile.menuLink,
          });
        }

        if (changedLinks.length === 0) {
          setEditingSections((current) => ({ ...current, [section]: false }));
          setToastMessage("No link changes to save");
          return;
        }

        setSavingSection(section);
        await updateSocialLinks.mutateAsync({
          locationId: selectedLocationId,
          data: {
            organisation_id: userProfile.organisation_id,
            links: changedLinks.map((link) => ({
              name: link.name,
              uri: link.value,
            })),
          },
        });
        await refreshProfileAfterSave();
        setEditingSections((current) => ({ ...current, [section]: false }));
        showSectionSavedToast(label);
      } catch (error) {
        const socialLinkError =
          error.response?.data?.errors?.links?.find((linkError) =>
            Boolean(linkError?.uri?.length)
          )?.uri?.[0];
        setToastMessage(
          socialLinkError ||
            error.response?.data?.message ||
            `Failed to update ${label.toLowerCase()}`
        );
      } finally {
        setSavingSection(null);
      }
      return;
    }

  };

  const updateHours = (index, field, value) => {
    setHours((current) =>
      current.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      )
    );
  };

  const updateMoreHours = (hoursTypeId, index, field, value) => {
    setMoreHours((current) =>
      current.map((item) =>
        item.hoursTypeId === hoursTypeId
          ? {
              ...item,
              hours: item.hours.map((day, dayIndex) =>
                dayIndex === index ? { ...day, [field]: value } : day
              ),
            }
          : item
      )
    );
  };

  const addMoreHours = (hoursType) => {
    setMoreHours((current) => [
      ...current,
      {
        hoursTypeId: hoursType.hoursTypeId,
        displayName: hoursType.displayName,
        hours: createClosedHours(),
      },
    ]);
  };

  const removeMoreHours = (hoursTypeId) => {
    setMoreHours((current) =>
      current.filter((item) => item.hoursTypeId !== hoursTypeId)
    );
  };

  const openSpecialHoursDialog = () => {
    setSpecialHoursDraft(specialHours.map((item) => ({ ...item })));
    setIsSpecialHoursDialogOpen(true);
  };

  const updateSpecialHoursDraft = (index, field, value) => {
    setSpecialHoursDraft((current) =>
      current.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      )
    );
  };

  const addSpecialHoursDate = () => {
    setSpecialHoursDraft((current) => [
      ...current,
      {
        date: "",
        status: "Closed",
        opens: "09:00",
        closes: "17:00",
      },
    ]);
  };

  const saveSpecialHours = async () => {
    if (specialHoursDraft.some((item) => !item.date)) {
      setToastMessage("Choose a date for every holiday-hours entry");
      return;
    }
    if (
      new Set(specialHoursDraft.map((item) => item.date)).size !==
      specialHoursDraft.length
    ) {
      setToastMessage("Each holiday date can only be added once");
      return;
    }

    setSavingSection("specialHours");
    try {
      await updateOpeningHours.mutateAsync({
        locationId: selectedLocationId,
        data: {
          organisation_id: userProfile.organisation_id,
          special_hours: specialHoursDraft,
        },
      });
      await refreshProfileAfterSave();
      setIsSpecialHoursDialogOpen(false);
      showSectionSavedToast("Holiday hours");
    } catch (error) {
      setToastMessage(
        error.response?.data?.message || "Failed to update holiday hours"
      );
    } finally {
      setSavingSection(null);
    }
  };

  const requestBasicInformationSave = () => {
    const originalName =
      sectionSnapshots.basic?.profile?.businessName || profile.businessName;
    if (profile.businessName.trim() !== originalName.trim()) {
      setIsNameChangeDialogOpen(true);
      return;
    }
    saveSection("basic", "Basic information");
  };

  const openDescriptionDialog = () => {
    setDescriptionInstructions("");
    setDescriptionSuggestion("");
    setDescriptionGenerationError("");
    setIsDescriptionDialogOpen(true);
  };

  const improveDescriptionFromSuggestion = () => {
    if (!editingSections.basic) {
      startEditing("basic");
    }
    openDescriptionDialog();
  };

  const handleGenerateDescription = async () => {
    setDescriptionGenerationError("");

    try {
      const response = await generateDescription.mutateAsync({
        locationId: selectedLocationId,
        data: {
          organisation_id: userProfile.organisation_id,
          instructions: descriptionInstructions,
        },
      });
      setDescriptionSuggestion(response.data.suggestion);
    } catch (error) {
      setDescriptionGenerationError(
        error.response?.data?.message ||
          "Failed to generate a business description."
      );
    }
  };

  const handleApplyDescriptionSuggestion = () => {
    const suggestion = descriptionSuggestion.trim();
    if (!suggestion || suggestion.length > 750) return;

    updateProfile("description", suggestion);
    setIsDescriptionDialogOpen(false);
  };

  return (
    <Flex
      direction="column"
      gap="4"
      style={{
        width: "100%",
        height: "100%",
        minWidth: 0,
        overflowY: "auto",
        paddingRight: "10px",
      }}
    >
      <ToastAlert />
      {isBusinessProfileLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            zIndex: 1000,
          }}
        >
          <Progress size="1" duration="1" />
        </div>
      )}

      <Flex justify="between" align="center" gap="3" wrap="wrap">
        <Select.Root
          value={selectedLocationId}
          onValueChange={setSelectedLocationId}
        >
          <Select.Trigger style={{ minWidth: 190 }}>
            <Flex align="center" gap="2">
              <MapPin size={14} />
              <Text>
                {selectedLocation?.name || "Business profile preview"}
              </Text>
            </Flex>
          </Select.Trigger>
          <Select.Content>
            {locationsData.length > 0 ? (
              locationsData.map((location) => (
                <Select.Item key={location.id} value={location.id.toString()}>
                  {location.name}
                </Select.Item>
              ))
            ) : (
              <Select.Item value="preview">Business profile preview</Select.Item>
            )}
          </Select.Content>
        </Select.Root>

        <Flex align="center" gap="2">
          <Badge
            color={businessProfileError ? "red" : isBusinessProfileLoading ? "gray" : "green"}
            variant="soft"
            radius="full"
          >
            <RefreshCw size={12} />
            {businessProfileError
              ? "Not synced"
              : isBusinessProfileLoading
                ? "Syncing with Google"
                : "Synced with Google"}
          </Badge>
          <Button
            size="1"
            color="gray"
            variant="outline"
            type="button"
            disabled={isBusinessProfileLoading || Boolean(businessProfileError)}
            onClick={() =>
              window.open(
                profile.metadata?.mapsUri || "https://business.google.com/",
                "_blank",
                "noopener,noreferrer"
              )
            }
          >
            View on Google
            <ExternalLink size={13} />
          </Button>
        </Flex>
      </Flex>

      {businessProfileError && (
        <Card
          size="3"
          style={{
            width: "100%",
            minHeight: 280,
            flexShrink: 0,
          }}
        >
          <Flex
            direction="column"
            align="center"
            justify="center"
            gap="4"
            p="5"
            style={{ minHeight: 230, textAlign: "center" }}
          >
            <Flex
              align="center"
              justify="center"
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                background: "var(--red-a3)",
              }}
            >
              <AlertTriangle size={22} color="var(--red-9)" />
            </Flex>

            <Flex direction="column" align="center" gap="2">
              <Text size="4" weight="bold">
                Business profile unavailable
              </Text>
              <Text size="2" color="gray" style={{ maxWidth: 560 }}>
                {businessProfileError.response?.status === 404
                  ? "This B-connect location is not mapped to a Google Business Profile location. Map it from Reviews Locations, then try again."
                  : businessProfileError.response?.data?.message ||
                    "Google could not return this business profile. Please try again."}
              </Text>
            </Flex>

            <Flex gap="2" justify="center" wrap="wrap">
              <Button
                size="2"
                variant="outline"
                type="button"
                onClick={() => refetchBusinessProfile()}
              >
                <RefreshCw size={14} />
                Try again
              </Button>
              {businessProfileError.response?.status === 404 && (
                <Button
                  size="2"
                  variant="soft"
                  color="gray"
                  type="button"
                  onClick={() => navigate("/reviews/locations")}
                >
                  Manage locations
                </Button>
              )}
            </Flex>
          </Flex>
        </Card>
      )}

      {/* <Card
        size="2"
        style={{
          background: "var(--blue-a3)",
          borderLeft: "3px solid var(--blue-9)",
        }}
      >
        <Flex justify="between" align="center" gap="5" wrap="wrap">
          <Flex direction="column" gap="1" style={{ flex: "1 1 420px" }}>
            <Flex align="center" gap="1">
              <Sparkles size={14} color="var(--blue-11)" />
              <Text size="2" weight="bold" color="blue">
                AI profile suggestions
              </Text>
            </Flex>
            <Text size="2">
              Your profile is {PROFILE_STRENGTH}% complete. Adding a business
              description and updating your opening hours could increase your
              profile visibility.
            </Text>
          </Flex>
          <Flex direction="column" gap="2" style={{ width: 210 }}>
            <Flex justify="between">
              <Text size="1" color="gray">
                Profile strength
              </Text>
              <Text size="1" weight="bold" color="blue">
                {PROFILE_STRENGTH}%
              </Text>
            </Flex>
            <div
              style={{
                height: 5,
                overflow: "hidden",
                borderRadius: "var(--radius-full)",
                background: "var(--gray-a4)",
              }}
            >
              <div
                style={{
                  width: `${PROFILE_STRENGTH}%`,
                  height: "100%",
                  background: "var(--blue-9)",
                }}
              />
            </div>
          </Flex>
        </Flex>
      </Card> */}

      <Grid
        columns={{ initial: "1", md: "2fr 1fr" }}
        gap="4"
        width="auto"
        style={{
          display:
            businessProfileError ? "none" : undefined,
          flexShrink: 0,
        }}
      >
        <Flex direction="column" gap="4" style={{ minWidth: 0 }}>
          <ProfileCard
            title="Basic Information"
            action={
              <SectionActions
                isEditing={editingSections.basic}
                canEdit={Boolean(businessProfileResponse?.data?.profile)}
                isSaving={
                  updateBasicInformation.isPending || savingSection === "basic"
                }
                onEdit={() => startEditing("basic")}
                onCancel={() => cancelEditing("basic")}
                onSave={requestBasicInformationSave}
              />
            }
          >
            <GoogleSyncNotice
              pendingFields={pendingFieldsForSection("basic")}
              publishedProfile={googleUpdateStatus.publishedProfile}
            />
            {isBusinessProfileLoading ? (
              <SectionLoading />
            ) : editingSections.basic ? (
              <>
                <Flex direction="column" gap="2">
                  <SectionLabel>Business name</SectionLabel>
                  <TextField.Root
                    value={profile.businessName}
                    onChange={(event) =>
                      updateProfile("businessName", event.target.value)
                    }
                  />
                  <Flex align="center" gap="1">
                    <AlertTriangle size={13} color="var(--orange-9)" />
                    <Text size="1" color="orange">
                      Changing your business name may require re-verification by
                      Google.
                    </Text>
                  </Flex>
                </Flex>

                <Flex direction="column" gap="2">
                  <SectionLabel>Business category (primary)</SectionLabel>
                  <CategoryPicker
                    organisationId={userProfile.organisation_id}
                    locationId={selectedLocationId}
                    regionCode={profile.storefrontAddress?.regionCode}
                    languageCode={profile.languageCode || "en"}
                    value={{
                      name: profile.primaryCategoryId,
                      displayName: profile.primaryCategory,
                    }}
                    placeholder="Select a primary category"
                    excludedNames={profile.additionalCategoryOptions.map(
                      (category) => category.name
                    )}
                    onSelect={(category) =>
                      updateProfileFields({
                        primaryCategoryId: category.name,
                        primaryCategory: category.displayName,
                      })
                    }
                  />
                </Flex>

                <Flex direction="column" gap="2">
                  <SectionLabel>Additional categories</SectionLabel>
                  <Flex gap="2" wrap="wrap">
                    {profile.additionalCategoryOptions.map((category) => (
                      <Badge key={category.name} color="blue" variant="soft">
                        {category.displayName}
                        <IconButton
                          size="1"
                          variant="ghost"
                          color="blue"
                          type="button"
                          aria-label={`Remove ${category.displayName}`}
                          onClick={() => {
                            const nextCategories =
                              profile.additionalCategoryOptions.filter(
                                (item) => item.name !== category.name
                              );
                            updateProfileFields({
                              additionalCategoryOptions: nextCategories,
                              additionalCategories: nextCategories.map(
                                (item) => item.displayName
                              ),
                            });
                          }}
                        >
                          <X size={11} />
                        </IconButton>
                      </Badge>
                    ))}
                  </Flex>
                  {profile.additionalCategoryOptions.length < 9 && (
                    <CategoryPicker
                      organisationId={userProfile.organisation_id}
                      locationId={selectedLocationId}
                      regionCode={profile.storefrontAddress?.regionCode}
                      languageCode={profile.languageCode || "en"}
                      placeholder="Add category"
                      excludedNames={[
                        profile.primaryCategoryId,
                        ...profile.additionalCategoryOptions.map(
                          (category) => category.name
                        ),
                      ]}
                      onSelect={(category) => {
                        const nextCategories = [
                          ...profile.additionalCategoryOptions,
                          category,
                        ];
                        updateProfileFields({
                          additionalCategoryOptions: nextCategories,
                          additionalCategories: nextCategories.map(
                            (item) => item.displayName
                          ),
                        });
                      }}
                    />
                  )}
                </Flex>

                <Flex direction="column" gap="2">
                  <Flex justify="between" align="center">
                    <SectionLabel>Business description</SectionLabel>
                    <Button
                      size="1"
                      variant="outline"
                      type="button"
                      onClick={openDescriptionDialog}
                    >
                      <Sparkles size={12} />
                      Improve with AI
                    </Button>
                  </Flex>
                  <TextArea
                    resize="vertical"
                    rows={5}
                    maxLength={750}
                    value={profile.description}
                    onChange={(event) =>
                      updateProfile("description", event.target.value)
                    }
                  />
                  <Text size="1" color="gray" align="right">
                    {profile.description.length} / 750
                  </Text>
                </Flex>
              </>
            ) : (
              <>
                <ReadOnlyField label="Business name" strong>
                  {profile.businessName}
                </ReadOnlyField>
                <Flex align="center" gap="2">
                  <AlertTriangle size={14} color="var(--orange-9)" />
                  <Text size="2" color="orange" style={{ fontStyle: "italic" }}>
                    Changing your business name may require re-verification by
                    Google.
                  </Text>
                </Flex>
                <ReadOnlyField label="Business category (primary)">
                  {profile.primaryCategory}
                </ReadOnlyField>
                <Flex direction="column" gap="2">
                  <SectionLabel>Additional categories</SectionLabel>
                  <Flex gap="2" wrap="wrap">
                    {profile.additionalCategories.map((category) => (
                      <Badge key={category} color="blue" variant="soft" size="2">
                        {category}
                      </Badge>
                    ))}
                  </Flex>
                </Flex>
                <ReadOnlyField label="Business description">
                  {profile.description}
                </ReadOnlyField>
                <Text size="1" color="gray" align="right">
                  {profile.description.length} / 750
                </Text>
              </>
            )}
          </ProfileCard>

          <ProfileCard
            title="Contact & Location"
            action={
              <SectionActions
                isEditing={editingSections.contact}
                canEdit={Boolean(businessProfileResponse?.data?.profile)}
                isSaving={
                  updateContactLocation.isPending || savingSection === "contact"
                }
                onEdit={() => startEditing("contact")}
                onCancel={() => cancelEditing("contact")}
                onSave={() => saveSection("contact", "Contact and location")}
              />
            }
          >
            <GoogleSyncNotice
              pendingFields={pendingFieldsForSection("contact")}
              publishedProfile={googleUpdateStatus.publishedProfile}
            />
            {isBusinessProfileLoading ? (
              <SectionLoading />
            ) : editingSections.contact ? (
              <>
                <Flex direction="column" gap="2">
                  <SectionLabel>Phone number</SectionLabel>
                  <TextField.Root
                    value={profile.phoneNumber}
                    onChange={(event) =>
                      updateProfile("phoneNumber", event.target.value)
                    }
                  />
                </Flex>
                <Flex direction="column" gap="2">
                  <SectionLabel>Website</SectionLabel>
                  <TextField.Root
                    value={profile.website}
                    onChange={(event) =>
                      updateProfile("website", event.target.value)
                    }
                  />
                </Flex>
                <Flex direction="column" gap="2">
                  <SectionLabel>Street address</SectionLabel>
                  <TextField.Root
                    value={profile.addressLine}
                    disabled={!profile.storefrontAddress?.regionCode}
                    onChange={(event) =>
                      updateProfile("addressLine", event.target.value)
                    }
                  />
                  <Grid columns={{ initial: "1", sm: "2" }} gap="3">
                    <Flex direction="column" gap="2">
                      <SectionLabel>City</SectionLabel>
                      <TextField.Root
                        value={profile.storefrontAddress?.locality || ""}
                        disabled={!profile.storefrontAddress?.regionCode}
                        onChange={(event) =>
                          updateStorefrontAddress("locality", event.target.value)
                        }
                      />
                    </Flex>
                    <Flex direction="column" gap="2">
                      <SectionLabel>Postal code</SectionLabel>
                      <TextField.Root
                        value={profile.storefrontAddress?.postalCode || ""}
                        disabled={!profile.storefrontAddress?.regionCode}
                        onChange={(event) =>
                          updateStorefrontAddress("postalCode", event.target.value)
                        }
                      />
                    </Flex>
                  </Grid>
                  {editableAddressPreview && (
                    <Text size="1" color="gray">
                      Full address: {editableAddressPreview}
                    </Text>
                  )}
                  {!profile.storefrontAddress?.regionCode && (
                    <Text size="1" color="gray">
                      Google did not return a structured address, so this field is read-only.
                    </Text>
                  )}
                </Flex>
              </>
            ) : (
              <>
                <ReadOnlyField label="Phone number">
                  {profile.phoneNumber}
                </ReadOnlyField>
                <ReadOnlyField label="Website">{profile.website}</ReadOnlyField>
                <ReadOnlyField label="Address">
                  {profile.address}
                </ReadOnlyField>
              </>
            )}
          </ProfileCard>

          <ProfileCard
            title="Opening Hours"
            action={
              <SectionActions
                isEditing={editingSections.hours}
                canEdit={
                  Boolean(businessProfileResponse?.data?.profile) &&
                  !hasSplitOpeningHours &&
                  !hasSplitMoreHours
                }
                isSaving={
                  updateOpeningHours.isPending || savingSection === "hours"
                }
                onEdit={() => startEditing("hours")}
                onCancel={() => cancelEditing("hours")}
                onSave={() => saveSection("hours", "Opening hours")}
              />
            }
          >
            <GoogleSyncNotice
              pendingFields={pendingFieldsForSection("hours")}
              publishedProfile={googleUpdateStatus.publishedProfile}
            />
            {isBusinessProfileLoading ? (
              <SectionLoading />
            ) : (
              <>
                {(hasSplitOpeningHours || hasSplitMoreHours) && (
                  <Text size="1" color="orange">
                    This location has split hours. Editing is disabled until multiple
                    periods per day are supported.
                  </Text>
                )}
                {editingSections.hours ? (
              <Flex direction="column" gap="5">
                <WeeklyHoursEditor hours={hours} onChange={updateHours} />
                <Button
                  size="1"
                  variant="ghost"
                  type="button"
                  disabled={hasSplitSpecialHours}
                  onClick={openSpecialHoursDialog}
                  style={{ alignSelf: "flex-start" }}
                >
                  Set holiday hours
                </Button>
                {hasSplitSpecialHours && (
                  <Text size="1" color="orange">
                    Holiday-hour periods overlap on the same date, so they cannot
                    be edited here yet.
                  </Text>
                )}

                {moreHours.map((item) => (
                  <Flex key={item.hoursTypeId} direction="column" gap="3">
                    <Separator size="4" />
                    <Flex align="center" justify="between">
                      <Text size="4" weight="bold">{item.displayName}</Text>
                      <Button
                        size="1"
                        variant="ghost"
                        color="red"
                        type="button"
                        onClick={() => removeMoreHours(item.hoursTypeId)}
                      >
                        Remove
                      </Button>
                    </Flex>
                    <WeeklyHoursEditor
                      hours={item.hours}
                      onChange={(index, field, value) =>
                        updateMoreHours(item.hoursTypeId, index, field, value)
                      }
                    />
                  </Flex>
                ))}

                {availableMoreHoursTypes.length > 0 && (
                  <Flex direction="column" gap="3">
                    <Separator size="4" />
                    <Text size="4" weight="bold">Add more hours</Text>
                    <Text size="2" color="gray">
                      Add service-specific hours for a typical week.
                    </Text>
                    <Flex gap="2" wrap="wrap">
                      {availableMoreHoursTypes.map((hoursType) => (
                        <Button
                          key={hoursType.hoursTypeId}
                          size="2"
                          variant="outline"
                          color="gray"
                          type="button"
                          onClick={() => addMoreHours(hoursType)}
                        >
                          <Plus size={16} />
                          {hoursType.displayName}
                        </Button>
                      ))}
                    </Flex>
                  </Flex>
                )}
              </Flex>
                ) : (
              <Flex direction="column" gap="5">
                <WeeklyHoursList hours={hours} />
                <Separator size="4" />
                <Flex direction="column" gap="3">
                  <Flex align="center" justify="between" gap="3">
                    <Text size="4" weight="bold">Holiday hours</Text>
                    <Button
                      size="1"
                      variant="ghost"
                      type="button"
                      disabled={hasSplitSpecialHours}
                      onClick={openSpecialHoursDialog}
                    >
                      Set holiday hours
                    </Button>
                  </Flex>
                  {specialHours.length === 0 ? (
                    <Text size="2" color="gray">
                      No holiday hours added.
                    </Text>
                  ) : specialHours.map((item) => (
                    <Flex key={item.date} justify="between" gap="3" wrap="wrap">
                      <Text size="2" weight="medium">
                        {new Date(`${item.date}T00:00:00`).toLocaleDateString()}
                      </Text>
                      <Text size="2">
                        {item.status === "Closed"
                          ? "Closed"
                          : `${formatTime(item.opens)} – ${formatTime(item.closes)}`}
                      </Text>
                    </Flex>
                  ))}
                </Flex>
                <Separator size="4" />
                <Flex direction="column" gap="3">
                  <Text size="4" weight="bold">More hours</Text>
                  {moreHours.length === 0 ? (
                    <Text size="2" color="gray">
                      No additional hours added.
                    </Text>
                  ) : moreHours.map((item) => (
                    <Flex key={item.hoursTypeId} direction="column" gap="2">
                      <Text size="3" weight="bold">{item.displayName}</Text>
                      <WeeklyHoursList hours={item.hours} />
                    </Flex>
                  ))}
                </Flex>
              </Flex>
                )}
              </>
            )}
          </ProfileCard>

          <ProfileCard
            title="Attributes"
            action={
              <SectionActions
                isEditing={editingSections.attributes}
                canEdit={attributes.length > 0}
                isSaving={
                  updateAttributes.isPending || savingSection === "attributes"
                }
                onEdit={() => startEditing("attributes")}
                onCancel={() => cancelEditing("attributes")}
                onSave={() => saveSection("attributes", "Attributes")}
              />
            }
          >
            <GoogleSyncNotice
              pendingFields={pendingFieldsForSection("attributes")}
              publishedProfile={googleUpdateStatus.publishedProfile}
            />
            {isBusinessProfileLoading ? (
              <SectionLoading />
            ) : (
              <>
            <Text size="2" color="gray">
              What your venue offers
            </Text>
            <Grid columns={{ initial: "1", sm: "2" }} gapX="5">
              {attributes.map((attribute, index) => (
                <Flex
                  key={attribute.name}
                  justify="between"
                  align="center"
                  py="2"
                  style={{ borderBottom: "1px solid var(--gray-a4)" }}
                >
                  <Text size="2">{attribute.label}</Text>
                  <Flex align="center" gap="2">
                    <Switch
                      size="1"
                      color="green"
                      checked={attribute.enabled}
                      disabled={!editingSections.attributes}
                      onCheckedChange={(checked) =>
                        setAttributes((current) =>
                          current.map((attribute, attributeIndex) =>
                            attributeIndex === index
                              ? { ...attribute, enabled: checked }
                              : attribute
                          )
                        )
                      }
                    />
                    <Text size="1" color={attribute.enabled ? "green" : "gray"}>
                      {attribute.enabled ? "Yes" : "No"}
                    </Text>
                  </Flex>
                </Flex>
              ))}
            </Grid>
              </>
            )}
          </ProfileCard>

          <ProfileCard
            title="Social & Links"
            action={
              <SectionActions
                isEditing={editingSections.social}
                canEdit={
                  profile.socialLinks.length > 0 || profile.menuLinkSupported
                }
                isSaving={
                  updateSocialLinks.isPending || savingSection === "social"
                }
                onEdit={() => startEditing("social")}
                onCancel={() => cancelEditing("social")}
                onSave={() => saveSection("social", "Social links")}
              />
            }
          >
            <GoogleSyncNotice
              pendingFields={pendingFieldsForSection("social")}
              publishedProfile={googleUpdateStatus.publishedProfile}
            />
            {isBusinessProfileLoading ? (
              <SectionLoading />
            ) : editingSections.social ? (
              <>
                <ReadOnlyField label="Website">{profile.website}</ReadOnlyField>
                <Text size="1" color="gray">
                  The website is edited from Contact & Location.
                </Text>
                {profile.menuLinkSupported && (
                  <Flex direction="column" gap="2">
                    <SectionLabel>{profile.menuLinkLabel}</SectionLabel>
                    <TextField.Root
                      type="url"
                      color={
                        getSocialLinkError({
                          name: "attributes/url_menu",
                          value: profile.menuLink,
                        })
                          ? "red"
                          : undefined
                      }
                      placeholder="https://yourbusiness.com/menu"
                      value={profile.menuLink}
                      onChange={(event) =>
                        updateProfile("menuLink", event.target.value)
                      }
                    />
                    {getSocialLinkError({
                      name: "attributes/url_menu",
                      value: profile.menuLink,
                    }) && (
                      <Text size="1" color="red">
                        {getSocialLinkError({
                          name: "attributes/url_menu",
                          value: profile.menuLink,
                        })}
                      </Text>
                    )}
                  </Flex>
                )}
                {profile.socialLinks.map((link, index) => {
                  const validationError = getSocialLinkError(link);
                  return (
                    <Flex key={link.name} direction="column" gap="2">
                      <SectionLabel>{link.label}</SectionLabel>
                      <TextField.Root
                        type="url"
                        color={validationError ? "red" : undefined}
                        placeholder={
                          SOCIAL_LINK_RULES[link.name]?.example || "https://..."
                        }
                        value={link.value}
                        onChange={(event) => {
                          const nextLinks = profile.socialLinks.map(
                            (item, itemIndex) =>
                              itemIndex === index
                                ? { ...item, value: event.target.value }
                                : item
                          );
                          updateProfile("socialLinks", nextLinks);
                        }}
                      />
                      {validationError && (
                        <Text size="1" color="red">
                          {validationError}
                        </Text>
                      )}
                    </Flex>
                  );
                })}
              </>
            ) : (
              <Flex direction="column" gap="3">
                <ReadOnlyField label="Website">{profile.website}</ReadOnlyField>
                {profile.menuLinkSupported && (
                  <ReadOnlyField label={profile.menuLinkLabel}>
                    {profile.menuLink || "Not added"}
                  </ReadOnlyField>
                )}
                {profile.socialLinks.filter((link) => link.value).length > 0 ? (
                  profile.socialLinks
                    .filter((link) => link.value)
                    .map((link) => (
                      <ReadOnlyField key={link.name} label={link.label}>
                        {link.value}
                      </ReadOnlyField>
                    ))
                ) : (
                  <Text size="2" color="gray">
                    No social links have been added on Google.
                  </Text>
                )}
              </Flex>
            )}
          </ProfileCard>

        </Flex>

        <Flex direction="column" gap="4" style={{ minWidth: 0 }}>
          <Card size="3">
            <Flex direction="column" gap="4">
              <Text size="3" weight="bold">
                Profile completeness
              </Text>
              {isBusinessProfileLoading ? (
                <SectionLoading />
              ) : (
                <>
                  <Flex justify="center">
                    <CompletionRing strength={profileStrength} />
                  </Flex>
                  <Flex direction="column">
                    {profileChecklist.map(([label, complete], index) => (
                      <Flex
                        key={label}
                        align="center"
                        gap="2"
                        py="2"
                        style={{
                          borderBottom:
                            index < profileChecklist.length - 1
                              ? "1px solid var(--gray-a4)"
                              : "none",
                        }}
                      >
                        {complete ? (
                          <CheckCircle2 size={14} color="var(--green-9)" />
                        ) : (
                          <X size={14} color="var(--gray-8)" />
                        )}
                        <Text size="2" color={complete ? undefined : "gray"}>
                          {label}
                        </Text>
                      </Flex>
                    ))}
                  </Flex>
                  <Text size="1" color="gray" style={{ fontStyle: "italic" }}>
                    Complete your profile to increase visibility on Google.
                  </Text>
                </>
              )}
            </Flex>
          </Card>

          <Card size="3">
            <Flex direction="column" gap="4">
              <Flex direction="column" gap="1">
                <Text size="3" weight="bold">
                  Suggestions
                </Text>
                <Text size="1" color="gray">
                  Based on your Google Business Profile
                </Text>
              </Flex>

              <Flex gap="3" align="start">
                <Flame size={16} color="var(--orange-9)" />
                <Flex direction="column" gap="2">
                  <Text size="2">
                    Your description could be more specific. Mention your key
                    products, services, or specialities.
                  </Text>
                  <Button
                    size="1"
                    variant="ghost"
                    type="button"
                    onClick={improveDescriptionFromSuggestion}
                    style={{ alignSelf: "flex-start" }}
                  >
                    Improve →
                  </Button>
                </Flex>
              </Flex>

              <Separator size="4" />

              <Flex gap="3" align="start">
                <Lightbulb size={16} color="var(--blue-9)" />
                <Flex direction="column" gap="2">
                  <Text size="2">
                    Businesses with special holiday hours get more profile
                    views.
                  </Text>
                  <Button
                    size="1"
                    variant="ghost"
                    type="button"
                    disabled={hasSplitSpecialHours}
                    onClick={openSpecialHoursDialog}
                    style={{ alignSelf: "flex-start" }}
                  >
                    Set hours →
                  </Button>
                </Flex>
              </Flex>

              {needsMorePhotos && (
                <>
                  <Separator size="4" />

                  <Flex gap="3" align="start">
                    <Image size={16} color="var(--green-9)" />
                    <Flex direction="column" gap="2">
                      <Text size="2">
                        Add at least 5 photos to complete your profile. Use the
                        Posts page to schedule them.
                      </Text>
                      <Button
                        size="1"
                        variant="ghost"
                        type="button"
                        onClick={() => navigate("/reviews/posts")}
                        style={{ alignSelf: "flex-start" }}
                      >
                        Go to Posts →
                      </Button>
                    </Flex>
                  </Flex>
                </>
              )}
            </Flex>
          </Card>
        </Flex>
      </Grid>

      <Dialog.Root
        open={isSpecialHoursDialogOpen}
        onOpenChange={setIsSpecialHoursDialogOpen}
      >
        <Dialog.Content maxWidth="760px">
          <Dialog.Title>Holiday hours</Dialog.Title>
          <Dialog.Description size="2" color="gray">
            Add dates when this location has different hours or is closed.
          </Dialog.Description>

          <Flex direction="column" gap="4" mt="4">
            {specialHoursDraft.length === 0 ? (
              <Text size="2" color="gray">
                No holiday hours have been added.
              </Text>
            ) : specialHoursDraft.map((item, index) => (
              <Card key={`${item.date}-${index}`} size="2">
                <Flex align="end" gap="3" wrap="wrap">
                  <Flex direction="column" gap="2" style={{ minWidth: 160 }}>
                    <SectionLabel>Date</SectionLabel>
                    <TextField.Root
                      type="date"
                      value={item.date}
                      onChange={(event) =>
                        updateSpecialHoursDraft(index, "date", event.target.value)
                      }
                    />
                  </Flex>
                  <Flex direction="column" gap="2" style={{ minWidth: 110 }}>
                    <SectionLabel>Status</SectionLabel>
                    <Select.Root
                      value={item.status}
                      onValueChange={(value) =>
                        updateSpecialHoursDraft(index, "status", value)
                      }
                    >
                      <Select.Trigger />
                      <Select.Content>
                        <Select.Item value="Open">Open</Select.Item>
                        <Select.Item value="Closed">Closed</Select.Item>
                      </Select.Content>
                    </Select.Root>
                  </Flex>
                  <Flex direction="column" gap="2" style={{ minWidth: 120 }}>
                    <SectionLabel>Opens</SectionLabel>
                    <TextField.Root
                      type="time"
                      step="60"
                      value={item.opens}
                      disabled={item.status === "Closed"}
                      onChange={(event) =>
                        updateSpecialHoursDraft(index, "opens", event.target.value)
                      }
                      aria-label={`${item.date} opening time`}
                    />
                  </Flex>
                  <Flex direction="column" gap="2" style={{ minWidth: 120 }}>
                    <SectionLabel>Closes</SectionLabel>
                    <TextField.Root
                      type="time"
                      step="60"
                      value={item.closes}
                      disabled={item.status === "Closed"}
                      onChange={(event) =>
                        updateSpecialHoursDraft(index, "closes", event.target.value)
                      }
                      aria-label={`${item.date} closing time`}
                    />
                  </Flex>
                  <IconButton
                    type="button"
                    variant="ghost"
                    color="red"
                    aria-label="Remove holiday date"
                    onClick={() =>
                      setSpecialHoursDraft((current) =>
                        current.filter((_, itemIndex) => itemIndex !== index)
                      )
                    }
                  >
                    <X size={16} />
                  </IconButton>
                </Flex>
              </Card>
            ))}

            <Button
              type="button"
              variant="outline"
              onClick={addSpecialHoursDate}
              style={{ alignSelf: "flex-start" }}
            >
              <Plus size={15} />
              Add date
            </Button>

            <Flex justify="end" gap="2">
              <Button
                type="button"
                variant="soft"
                color="gray"
                disabled={savingSection === "specialHours"}
                onClick={() => setIsSpecialHoursDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                disabled={savingSection === "specialHours"}
                onClick={saveSpecialHours}
              >
                {savingSection === "specialHours" ? "Saving..." : "Save"}
              </Button>
            </Flex>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      <Dialog.Root
        open={isDescriptionDialogOpen}
        onOpenChange={setIsDescriptionDialogOpen}
      >
        <Dialog.Content maxWidth="620px">
          <Dialog.Title>Improve business description</Dialog.Title>
          <Dialog.Description size="2" color="gray">
            Generate a suggestion from the live Google Business Profile. The
            suggestion will not be sent to Google until you apply it and save
            Basic Information.
          </Dialog.Description>

          <Flex direction="column" gap="4" mt="4">
            <Flex direction="column" gap="2">
              <Text size="2" weight="bold">
                Current description
              </Text>
              <TextArea
                value={profile.description}
                readOnly
                resize="vertical"
                rows={4}
                placeholder="No current description"
              />
            </Flex>

            <Flex direction="column" gap="2">
              <Text size="2" weight="bold">
                How should AI improve it?
              </Text>
              <TextArea
                value={descriptionInstructions}
                maxLength={500}
                placeholder="For example: Make it warm, professional and concise."
                onChange={(event) =>
                  setDescriptionInstructions(event.target.value)
                }
              />
              <Text size="1" color="gray" align="right">
                {descriptionInstructions.length} / 500
              </Text>
            </Flex>

            <Button
              type="button"
              variant="outline"
              disabled={generateDescription.isPending}
              onClick={handleGenerateDescription}
            >
              <Sparkles size={14} />
              {generateDescription.isPending
                ? "Generating..."
                : descriptionSuggestion
                  ? "Regenerate"
                  : "Generate suggestion"}
            </Button>

            {descriptionGenerationError && (
              <Text size="2" color="red">
                {descriptionGenerationError}
              </Text>
            )}

            {descriptionSuggestion && (
              <Flex direction="column" gap="2">
                <Text size="2" weight="bold">
                  AI suggestion
                </Text>
                <TextArea
                  value={descriptionSuggestion}
                  maxLength={750}
                  resize="vertical"
                  rows={7}
                  onChange={(event) =>
                    setDescriptionSuggestion(event.target.value)
                  }
                />
                <Text
                  size="1"
                  color={descriptionSuggestion.length > 750 ? "red" : "gray"}
                  align="right"
                >
                  {descriptionSuggestion.length} / 750
                </Text>
              </Flex>
            )}

            <Flex justify="end" gap="3">
              <Dialog.Close>
                <Button type="button" color="gray" variant="soft">
                  Cancel
                </Button>
              </Dialog.Close>
              <Button
                type="button"
                disabled={
                  !descriptionSuggestion.trim() ||
                  descriptionSuggestion.length > 750 ||
                  generateDescription.isPending
                }
                onClick={handleApplyDescriptionSuggestion}
              >
                Apply suggestion
              </Button>
            </Flex>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      <AlertDialog.Root
        open={isNameChangeDialogOpen}
        onOpenChange={setIsNameChangeDialogOpen}
      >
        <AlertDialog.Content maxWidth="480px">
          <AlertDialog.Title>Confirm business name change</AlertDialog.Title>
          <AlertDialog.Description size="2">
            You are changing the Google Business Profile name from{" "}
            <strong>
              {sectionSnapshots.basic?.profile?.businessName || "the current name"}
            </strong>{" "}
            to <strong>{profile.businessName}</strong>. Google may review this
            change or require the business to be verified again.
          </AlertDialog.Description>
          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button
                onClick={() => saveSection("basic", "Basic information")}
              >
                Confirm and save
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </Flex>
  );
}
